import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { GridColDef } from '@mui/x-data-grid-premium';
import {
  ConditionalTemplateFilter_ConditionalTemplateFilterDataGridPageFilter,
  ConditionalTemplateFilter_FieldsFragment,
  ConditionalTemplateFilterDataGridColumns,
  useConditionalTemplateFilter_AvailableConditionalTemplateFiltersByFilterQuestionIdLazyQuery,
  useConditionalTemplateFilter_GetAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQuery,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionByIdQuery,
  useConditionalTemplateQuestion_GetConditionalTemplateQuestionsByConditionalTemplateQuestionSetIdQuery,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import HomeLayout from 'layouts/home-layout';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import ConditionalTemplateFilterActions from './components/conditional-template-filter-actions';
import CreateConditionalTemplateFilterModal from './components/create-conditional-template-filter-modal';
import EditConditionalTemplateFilterModal from './components/edit-conditional-template-filter-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  button: { marginBottom: '10px' },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  header: {
    marginRight: '10px',
  },
}));

export default function ManageConditionalTemplateFilters() {
  const classes = useStyles();
  const history = useHistory();

  const { conditionalTemplateQuestionSetId, conditionalTemplateQuestionId } =
    useParams<Record<string, string>>();

  const [selectedConditionalTemplateFilter, setSelectedConditionalTemplateFilter] = useState<
    ConditionalTemplateFilter_FieldsFragment | undefined
  >(undefined);

  const [showCreateConditionalTemplateFilterModal, setShowCreateConditionalTemplateFilterModal] =
    useState<boolean>(false);

  const [showEditConditionalTemplateFilterModal, setShowEditConditionalTemplateFilterModal] =
    useState<boolean>(false);

  const {
    data: conditionalTemplateQuestionsData,
    loading: conditionalTemplateQuestionsDataLoading,
  } =
    useConditionalTemplateQuestion_GetConditionalTemplateQuestionsByConditionalTemplateQuestionSetIdQuery(
      {
        variables: {
          input: { conditionalTemplateQuestionSetId: conditionalTemplateQuestionSetId },
        },
      },
    );

  const { data: conditionalTemplateQuestionData, loading: conditionalTemplateQuestionDataLoading } =
    useConditionalTemplateQuestion_GetConditionalTemplateQuestionByIdQuery({
      variables: { id: conditionalTemplateQuestionId },
    });

  const [
    getAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQuery,
    getAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQueryResult,
  ] =
    useConditionalTemplateFilter_GetAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQuery(
      {
        variables: {
          input: {
            conditionalTemplateQuestionId: conditionalTemplateQuestionId,
            paginationInput: {
              page: defaultPaginationModel.page,
              pageSize: defaultPaginationModel.pageSize,
            },
          },
        },
      },
    );

  const [filterQuestionHasAvailableFilters] =
    useConditionalTemplateFilter_AvailableConditionalTemplateFiltersByFilterQuestionIdLazyQuery({
      fetchPolicy: 'network-only',
      async onCompleted(data) {
        if (data.data.ok) {
          setShowCreateConditionalTemplateFilterModal(true);
          return;
        }
        showToast({
          message: 'No available question / answer combinations for this filter question',
          severity: NotificationSeverity.INFO,
        });
        setShowCreateConditionalTemplateFilterModal(false);
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting all filter answers: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const columns: GridColDef<ConditionalTemplateFilter_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: ConditionalTemplateFilterDataGridColumns.FilterQuestion,
      filterable: false,
      sortable: false,
      flex: 2,
      headerName: 'Filter Question',
      renderCell: ({ row }) => {
        return row.filterQuestion?.question;
      },
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateFilterDataGridColumns.FilterAnswer,
      filterable: false,
      sortable: false,
      flex: 2,
      headerName: 'Filter Answer',
      renderCell: ({ row }) => {
        return row.filterAnswer?.answer;
      },
    },
    {
      disableColumnMenu: true,
      field: ConditionalTemplateFilterDataGridColumns.SelectedQuestionAnswers,
      filterable: false,
      sortable: false,
      flex: 2,
      headerName: 'Selected Question Answers Count',
      renderCell: ({ row }) => {
        return `${row.conditionalTemplateFilterToAnswers.length}`;
      },
    },

    {
      disableColumnMenu: true,
      field: 'actions',
      filterable: false,
      sortable: false,
      flex: 0.5,
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <ConditionalTemplateFilterActions
            conditionalTemplateFilter={row}
            onEditSelectedConditionalTemplateFilter={() => {
              setSelectedConditionalTemplateFilter(row);
              setShowEditConditionalTemplateFilterModal(true);
            }}
          />
        );
      },
    },
  ];

  return (
    <HomeLayout showPortalName={false}>
      <>
        {showEditConditionalTemplateFilterModal && selectedConditionalTemplateFilter && (
          <EditConditionalTemplateFilterModal
            conditionalTemplateQuestionId={conditionalTemplateQuestionId}
            showModal={showEditConditionalTemplateFilterModal}
            setShowModal={setShowEditConditionalTemplateFilterModal}
            setSelectedConditionalTemplateFilter={setSelectedConditionalTemplateFilter}
            selectedConditionalTemplateFilter={selectedConditionalTemplateFilter}
          />
        )}
        {showCreateConditionalTemplateFilterModal && (
          <CreateConditionalTemplateFilterModal
            conditionalTemplateQuestionId={conditionalTemplateQuestionId}
            showModal={showCreateConditionalTemplateFilterModal}
            setShowModal={setShowCreateConditionalTemplateFilterModal}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 2 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>

                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              <span
                className={classes.link}
                onClick={() => {
                  history.push(`/home/manage-conditional-template-question-sets`);
                }}
              >
                Conditional Question Sets
              </span>
              <span className={classes.routeIconContainer}>
                <ChevronRight />
              </span>
              <span
                className={classes.link}
                onClick={() => {
                  history.push(
                    `/home/${conditionalTemplateQuestionSetId}/manage-conditional-template-questions`,
                  );
                }}
              >
                Conditional Questions
              </span>
              <span className={classes.routeIconContainer}>
                <ChevronRight />
              </span>
              Manage Conditional Template Filters
            </Typography>
          </div>
          <Typography variant="body1" style={{ marginTop: '10px' }}>
            {conditionalTemplateQuestionData?.data.question}
          </Typography>
          <div className={classes.button}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              style={{ marginTop: '3px', padding: '2px 10px' }}
              onClick={async () => {
                const filterQuestion = conditionalTemplateQuestionsData?.data?.find(
                  (question) =>
                    question.sortOrder === conditionalTemplateQuestionData!.data!.sortOrder - 1,
                );

                if (!filterQuestion) {
                  showToast({
                    message:
                      'This is the first question in this set, this question cannot create a filter.',
                    severity: NotificationSeverity.INFO,
                  });
                  return;
                }
                filterQuestionHasAvailableFilters({
                  variables: {
                    input: {
                      conditionalTemplateQuestionId: conditionalTemplateQuestionId,
                      filterQuestionId: filterQuestion.id,
                    },
                  },
                });
              }}
            >
              + Create New Filter
            </Button>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <ServerEnabledDataGrid<ConditionalTemplateFilter_ConditionalTemplateFilterDataGridPageFilter>
                className={classes.root}
                gridStateKey={GridStateKey.ConditionalTemplateFilterGrid}
                queryResult={
                  getAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQueryResult
                }
                executeLazyQuery={
                  getAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQuery
                }
                loading={
                  getAllConditionalTemplateFiltersForConditionalTemplateFilterDataGridLazyQueryResult.loading ||
                  (conditionalTemplateQuestionDataLoading && !conditionalTemplateQuestionData) ||
                  (conditionalTemplateQuestionsDataLoading && !conditionalTemplateQuestionsData)
                }
                columns={columns}
                disableColumnReorder
                disableRowSelectionOnClick
                autoHeight={true}
                rowHeight={65}
                pagination
              />
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
